// modules
import debounce from 'lodash/debounce';
import SearchService from 'modules/Search';
import FormatService from 'modules/Format';
import ValidationService from 'modules/Validation';
import AppService from 'modules/App';
import AbstractSymbolsSearchService from 'modules/TradingChart/components/TradingView/utils/services/symbolsSearch/AbstractSymbolsSearchService';
import { DEFAULT_TOKEN } from 'app/routing/registration';
import SpecialTradingDates from 'modules/SpecialTradingDates';
import MarketHolidayDates from 'modules/MarketHolidayDates';
// constants
import {
  decomposeExchangeToken,
  decomposeSegment,
} from 'modules/BOD/modules/Search/utils';
import { Exchange } from 'modules/Search/constants';
import ResolutionInstance from './supportedResolutions';
import { DEBOUNCE_SEARCH_TIME, DEFAULT_SYMBOL_INFO } from './constants';
// typings
import { SearchItem, SearchResultItemAttributes } from './typings';
import {
  LibrarySymbolInfo,
  SearchSymbolResultItem,
  SearchSymbolsCallback,
  ResolutionString,
} from '../../../sources/charting_library.min';
// utils
import { removeReloadSuffix } from './utils';

class SymbolsSearchWebService extends AbstractSymbolsSearchService {
  debounceSearchTime = DEBOUNCE_SEARCH_TIME;

  private getSupportedResolutions() {
    return ResolutionInstance.supportedResolutions as ResolutionString[];
  }

  private getSupportedIntradayMultipliers() {
    return ResolutionInstance.supportedIntradayMultipliers;
  }

  private generateSymbolInformation(result: DataSchema) {
    const { displayName, name, exchange, exchangeToken, tickSize = 1 } = result;

    const { segment } = decomposeExchangeToken(exchangeToken);
    const { series } = decomposeSegment(segment);

    const isCurrency = ValidationService.isCurrency(segment);
    const isFnO = ValidationService.isFnO(segment);
    const isCommodity = ValidationService.isCommodity(segment);
    const isIndex = ValidationService.isIndex(series);
    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      displayName || '',
      exchangeToken || '',
    );
    const description = isFnO || isCurrency || isCommodity ? displayName : name;

    const correctionsfromSpecialHolidays =
     SpecialTradingDates.getSpecialHolidayRules(
      segment,
    );

    const sessionHolidays = MarketHolidayDates.getMarketHolidayDatesForTvCharts(
      segment,
    );
    const correctionsFromMarketHolidays =
    MarketHolidayDates.getCorrectionsFromHolidaysForTvCharts(
      segment,
    );

    const corrections = `${correctionsfromSpecialHolidays}${correctionsFromMarketHolidays}`;

    const symbolInformation: LibrarySymbolInfo = {
      exchange: segment,
      name: displayName,
      full_name: tvExchangeToken,
      listed_exchange: exchange,
      minmov: isCurrency ? tickSize * 100 : tickSize,
      // Muhurat trading special day
      corrections: corrections?.slice(0, -1),
      session_holidays: sessionHolidays.toString(),
      session: this.getSymbolSessionBySegment(exchangeToken),
      description,
      pricescale: isCurrency ? 10000 : 100,
      ticker: tvExchangeToken,
      visible_plots_set: isIndex ? 'ohlc' : 'ohlcv',
      ...DEFAULT_SYMBOL_INFO,
      supported_resolutions: this.getSupportedResolutions(),
      intraday_multipliers: [...this.getSupportedIntradayMultipliers(), '1D', '1W', '1M'],
    };

    return symbolInformation;
  }

  public async resolveSymbolByToken(
    tokenToSearch: string,
    onResult: (result: LibrarySymbolInfo) => void,
    onError: (reason: string) => void,
  ) {
    if (AppService.isOffline) {
      setTimeout(() => {
        onError('Currently Offline');
      }, 0);
      return;
    }

    const updatedExchangeToken = removeReloadSuffix(tokenToSearch);
    const result = await SearchService.searchByExchangeToken(
      updatedExchangeToken,
    );

    if (result) {
      const symbolInformation = this.generateSymbolInformation(result);
      onResult(symbolInformation);
    } else if (tokenToSearch !== DEFAULT_TOKEN) {
      // Retry with the DEFAULT_TOKEN if it's not already being used
      this.resolveSymbolByToken(DEFAULT_TOKEN, onResult, onError);
    } else {
      onError('Symbol not found after maximum attempts');
    }
  }

  debounceApiScripSearch = debounce(
    this.getSearchResults,
    this.debounceSearchTime,
  );

  public searchSymbolsByUserInput(
    userInput: string,
    exchange: Exchange,
    symbolType: SearchTabKeys = 'ALL',
    onResults: SearchSymbolsCallback,
  ) {
    // To perform Api Scrip Search
    return this.debounceApiScripSearch(userInput, symbolType, onResults);
  }

  public async getSearchResults(
    userInput: string,
    symbolType: SearchTabKeys = 'ALL',
    onResults: SearchSymbolsCallback,
  ) {
    if (userInput?.length < 2) {
      onResults([]);
      return;
    }

    const rawResults = await SearchService.scripSearch(userInput, symbolType);
    const searchItems: SearchSymbolResultItem[] = [];
    rawResults.forEach(item => {
      const exchangeToken = this.generateExchangeTokenFromScripSearchResult(
        item.attributes,
      );
      // For Index scrips, in ticker need to use attributes.name instead of deriveDisplayName to match with bod data
      const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
        item.attributes.instrumentType === 'INDEX'
          ? item.attributes.name
          : this.deriveDisplayName(item.attributes) || '',
        exchangeToken || '',
      );
      const searchResultData: SearchSymbolResultItem = {
        full_name: this.deriveDisplayName(item.attributes),
        exchange: item.attributes.exchange,
        type: item.attributes.instrumentType,
        symbol: this.deriveSymbol(item.attributes), // Provide a fallback if undefined
        description: item.attributes.name,
        ticker: tvExchangeToken,
      };
      searchItems.push(searchResultData);
    });

    onResults(searchItems);
  }

  private deriveSymbol = (
    attributes: SearchResultItemAttributes | SearchItem,
  ) => {
    const {
      instrumentType,
      assetSymbol,
      expiry,
      strikePrice,
      tradingSymbol,
    } = attributes;
    if (instrumentType === 'CE' || instrumentType === 'PE') {
      return `${assetSymbol} ${expiry?.replace(
        /\s/g,
        '',
      )} ${strikePrice} ${instrumentType}`.toUpperCase();
    }
    if (instrumentType === 'FUT') {
      return `${assetSymbol} ${expiry?.replace(
        /\s/g,
        '',
      )} ${instrumentType}`.toUpperCase();
    }
    return `${tradingSymbol}`.toUpperCase();
  };

  private deriveDisplayName = (
    attributes: SearchResultItemAttributes | SearchItem,
  ) => {
    const {
      instrumentType,
      assetSymbol,
      expiry,
      strikePrice,
      tradingSymbol,
      exchange,
    } = attributes;
    if (instrumentType === 'CE' || instrumentType === 'PE') {
      return `${assetSymbol} ${expiry?.replace(
        /\s/g,
        '',
      )} ${strikePrice} ${instrumentType}`.toUpperCase();
    }
    if (instrumentType === 'FUT') {
      return `${assetSymbol} ${expiry?.replace(
        /\s/g,
        '',
      )} ${instrumentType}`.toUpperCase();
    }
    if (exchange === Exchange.BSE) return `${tradingSymbol}`.toUpperCase();
    return `${tradingSymbol} ${instrumentType}`.toUpperCase();
  };

  private getTokenFromScripSearchResult = (
    scripInfo: SearchResultItemAttributes | SearchItem,
  ) => {
    const { instrumentType, instrumentKey, exchangeToken } = scripInfo;
    return instrumentType === 'INDEX'
      ? instrumentKey?.split?.('|')?.[1]
      : exchangeToken;
  };

  generateExchangeTokenFromScripSearchResult = (
    scripInfo: SearchResultItemAttributes | SearchItem,
    separator: '-' | '|' = '|',
  ) => {
    const { segment } = scripInfo;
    const token = this.getTokenFromScripSearchResult(scripInfo);
    const exchangeToken = FormatService.generateExchangeToken(
      segment,
      token,
      separator,
    );
    return exchangeToken;
  };
}

export default new SymbolsSearchWebService();
